import React, {
  useState,
  useMemo,
  useCallback,
  forwardRef,
  useEffect
} from 'react';
import ProductCardNew from './ProductCardNew';

import SuccessModalWithTimer from '@components/Modals/SuccessModalWithTimer';

import isEmpty from 'lodash/isEmpty';
import { numberTextFormat } from '@utils/declension';
import { observer } from 'mobx-react';
import useWindowSize from '@customHooks/useWindowSize';
import { useHistory } from '@compat/router';
import {
  prepareAllBadges,
  prepareBottomBadges,
  prepareNewBadges,
  prepareOldBadges
} from '@utils/prepareBadges';
import { preparePriceFloatObject } from '@utils/floatNumbers';
import ReviewProductModal from '@components/Modals/ReviewProductModal';
import icons from '@icons';
import type { CatalogItemCardT } from '@root/src/types';
import type {
  AddProductToPostponedListMutation,
  RemoveProductFromPostponedListMutation
} from '@schema/main';
import type { CatalogTasksItemsFragmentFragment } from '@schema/cart';
import type { TaskItem } from '@routes/ProductPage/index.type';
import { autorun } from 'mobx';
import { IsPrerender } from '@utils/check';
import { getPreorderDateLabel } from '@utils/getPreorderDateLabel';
import { useStores } from '@root/src/stores';

const {
  timer: { white: timerWhiteOutlined }
} = icons.Fill;

interface ProductCardProps {
  barMenuLinkPrefix?: string;
  isCatalogMobile?: boolean;
  isProductBigCards?: boolean;
  isShowLongBuyBtn?: boolean;
  isShowPostponed?: boolean;
  isShowTrash?: boolean;
  item: CatalogItemCardT;
  mutateOrder?: any;
  omImgHoverEnd?: any;
  onImgHover?: any;
  productOfDay?: any;
  removeProduct?: any;
  isLeaveCommentBtn?: boolean;
  onUpdateReview?: any;
  mutateOrderQuantity?: number;
  openInNewPage?: boolean;
  onAddToOrderFirst?: () => void;
  isBigMobileCard?: boolean;
  isStatic?: boolean;
}

export type ProductCardPropsArg = ProductCardProps;

const ProductCard = observer(
  forwardRef<HTMLAnchorElement, ProductCardProps>(
    (
      {
        barMenuLinkPrefix,
        isProductBigCards,
        isShowLongBuyBtn,
        isShowPostponed,
        isShowTrash,
        item,
        mutateOrder,
        omImgHoverEnd,
        onImgHover,
        productOfDay,
        removeProduct,
        isLeaveCommentBtn,
        onUpdateReview,
        mutateOrderQuantity,
        openInNewPage,
        onAddToOrderFirst,
        isBigMobileCard,
        isStatic
      },
      ref
    ) => {
      const { cartStore, catalogStore, modalsStore, userStore } = useStores();
      const { windowWidth } = useWindowSize();
      const [productInfo, setProductInfo] =
        useState<ProductCardProps['item']>(item);
      const [showCompleteModal, setShowCompleteModal] = useState(false);
      const [isFavorite, setIsFavorite] = useState(item.isFavorite);
      const [isPostponed, setIsPostponed] = useState(item.isPostponed);
      const [isBtnDisabled, setIsBtnDisabled] = useState(false);
      const [leaveReviewItemInfoModal, setLeaveReviewItemInfoModal] =
        useState(false);
      const history = useHistory();
      const { productsAmount, cart, amountStores } = cartStore;
      const { user } = userStore;
      const { changeAuthModalState } = modalsStore;

      useEffect(() => {
        setProductInfo(item);
        setIsFavorite(item.isFavorite);
        setIsPostponed(item.isPostponed);
      }, [item]);

      const {
        id,
        rating_comments,
        comments_number,
        fileItem,
        title,
        subtitle,
        isExpressPickupAval,
        sales_number,
        isPromo,
        active,
        measureItem,
        available,
        delivery,
        vesovoi,
        weight_of_goods,
        is_18plus,
        specialPriceData,
        priceBlockWeightPrice,
        war_id,
        gds_count,
        isExpressDeliveryAval,
        expiration_title,
        supply_title,
        discounPriceField,
        discounPriceField2,
        badges: itemBadges,
        badgesList,
        is_our,
        oldPriceField,
        oldPriceField2,
        currentPriceField,
        currentPriceField2,
        symbolPriceField,
        symbolPriceField2,
        promocodeGiftItems,
        taskItems
      } = productInfo;

      const productCount = isStatic
        ? 0
        : mutateOrder
        ? mutateOrderQuantity
        : productsAmount?.[id] || 0;
      const isProductInCart = productCount !== undefined && productCount > 0;

      const onChangeWeightTabHandler = (id: CatalogItemCardT['id']): void => {
        if (item.variants?.length) {
          const newItemInfo = item.variants.find(variant => variant.id === id);
          if (newItemInfo) {
            setProductInfo(newItemInfo as unknown as CatalogItemCardT);
          }
        }
      };

      const showCompleteModalFunc = (): void => {
        setShowCompleteModal(true);
      };

      const changeCompleteModalState = (): void => {
        setShowCompleteModal(false);
      };

      const addToFavourites = (isFavorite: boolean): void => {
        catalogStore
          .addToFavourites({
            postponed: false,
            action: isFavorite ? 'remove' : 'add',
            objectId: +id,
            object: 'catalog'
          })
          .then(() => {
            setIsFavorite(prev => !prev);

            if (!isFavorite) {
              window?.dataLayer?.push({
                event: 'webit_add_to_favorites',
                eventAction: 'add',
                productId: id
              });
            }
          });
      };

      const prepareToAddToFavourites = ({
        isFavorite,
        e
      }: {
        isFavorite: boolean;
        e: any;
      }): void => {
        e.preventDefault();
        e.stopPropagation();

        if (isEmpty(user)) {
          changeAuthModalState();
        } else {
          addToFavourites(isFavorite);
        }
      };

      const changePostponedStatus = (e: any): void => {
        e.preventDefault();
        e.stopPropagation();
        const method = isPostponed
          ? 'removeProductFromPostponed'
          : 'addProductToPostponed';

        cartStore[method](+id).then(
          (
            res:
              | RemoveProductFromPostponedListMutation['removeProductFromPostponedList']
              | AddProductToPostponedListMutation['addProductToPostponedList']
          ) => {
            if (res.success) {
              setIsPostponed(prev => !prev);
            }
          }
        );
      };

      const onAddToOrderClickHandler = useCallback(
        (e: MouseEvent, method: 'plus' | 'minus'): void => {
          e.stopPropagation();
          e.preventDefault();
          setIsBtnDisabled(true);
          mutateOrder(id, method);
        },
        [id, mutateOrder]
      );

      const removeProductHandler = useCallback(
        () =>
          ({
            product,
            e
          }: {
            product: CatalogItemCardT;
            e: MouseEvent;
          }): void => {
            e.preventDefault();
            e.stopPropagation();
            removeProduct(product);
          },
        [removeProduct]
      );

      const multiPricePromo =
        productInfo.multiPrices?.find(
          item => typeof item.buy_at_once === 'number' && item.buy_at_once > 0
        ) ?? null;

      const isBuyAtOncePromo =
        multiPricePromo &&
        typeof multiPricePromo?.buy_at_once === 'number' &&
        multiPricePromo?.buy_at_once > 0;

      const discount =
        isBuyAtOncePromo &&
        multiPricePromo?.price_active &&
        multiPricePromo?.price_retail &&
        productCount < Number(multiPricePromo?.buy_at_once)
          ? 100 -
            Math.round(
              (multiPricePromo?.price_active / multiPricePromo?.price_retail) *
                100
            )
          : priceBlockWeightPrice
          ? discounPriceField
          : discounPriceField2;

      const isDigital = item.digital === 1;

      const productAsGift = useMemo(
        () => promocodeGiftItems?.find(item => item.type === 'gift')?.giftItem,
        [promocodeGiftItems]
      );

      const preparedBadges = useMemo(
        () =>
          itemBadges
            ? prepareOldBadges(
                itemBadges,
                discount,
                isDigital,
                isBuyAtOncePromo ? multiPricePromo : null,
                productAsGift ? { id: productAsGift?.id } : null
              )
            : [],
        [
          itemBadges,
          discount,
          isDigital,
          isBuyAtOncePromo,
          multiPricePromo,
          productAsGift
        ]
      );
      const preparedNewBadges = useMemo(
        () => (badgesList ? prepareNewBadges(badgesList) : []),
        [badgesList]
      );

      const badges = useMemo(
        () =>
          isLeaveCommentBtn
            ? [
                {
                  id: 'review',
                  icon: timerWhiteOutlined,
                  link: null,
                  title: 'нет отзыва',
                  color: '#FC7464'
                }
              ]
            : prepareAllBadges([...preparedBadges, ...preparedNewBadges]),
        [isLeaveCommentBtn, preparedBadges, preparedNewBadges]
      );

      const badgesBottom = useMemo(
        () => (badgesList ? prepareBottomBadges(is_our, badgesList) : []),
        [badgesList, is_our]
      );

      const imgsArr = useMemo(
        () => fileItem?.cacheItem?.map(item => item?.filePath),
        [fileItem]
      );

      const salesNumberInfo = useMemo(
        () => numberTextFormat(sales_number || 0),
        [sales_number]
      );

      const onReviewProductModalClose = useCallback(() => {
        setLeaveReviewItemInfoModal(false);
      }, []);
      const leaveReviewHandler = useCallback(() => {
        setLeaveReviewItemInfoModal(true);
      }, []);

      const { store_id } = cart;
      const activeAmountStores = amountStores.findIndex(
        item => item.id === store_id
      );

      const isVesovoi = vesovoi === 'Y';

      const isPreOrder = getPreorderDateLabel(
        amountStores[activeAmountStores]?.nearestAvailableInterval?.value,
        delivery?.date_with_timezone,
        { isNearest: delivery?.is_nearest }
      );
      const supplyDate = supply_title ?? null;

      const _oldPriceField = preparePriceFloatObject(
        isBuyAtOncePromo &&
          multiPricePromo &&
          productCount < Number(multiPricePromo?.buy_at_once)
          ? priceBlockWeightPrice
            ? currentPriceField
            : isVesovoi
            ? oldPriceField2
            : discount
            ? oldPriceField
            : currentPriceField
          : priceBlockWeightPrice
          ? oldPriceField
          : oldPriceField2
      );
      const _currentPriceField = preparePriceFloatObject(
        isBuyAtOncePromo &&
          multiPricePromo &&
          productCount < Number(multiPricePromo?.buy_at_once)
          ? priceBlockWeightPrice
            ? multiPricePromo?.weightPriceData?.price1Kg
            : isVesovoi
            ? multiPricePromo?.weightPriceData?.price
            : multiPricePromo.price_active
          : priceBlockWeightPrice
          ? currentPriceField
          : currentPriceField2
      );

      const getTask = (
        tasksItems:
          | CatalogTasksItemsFragmentFragment['taskItems']
          | null
          | undefined
      ): TaskItem | null => {
        if (tasksItems?.task?.access?.length) {
          return { ...tasksItems.task.access[0], type: 'access' };
        } else if (tasksItems?.task?.progress?.length) {
          return { ...tasksItems.task.progress[0], type: 'progress' };
        } else {
          return null;
        }
      };

      const task = getTask(taskItems) ?? null;

      const settings = {
        link: barMenuLinkPrefix
          ? `${barMenuLinkPrefix}/${id}`
          : `/product/${id}`,
        isBarMenuCurrentPath: !!location.pathname.includes('/bar-menu/'),
        isMenu: !!item.isMenu,
        id,
        is_not_for_sale: item.is_not_for_sale,
        badges,
        title,
        is_18plus: !!is_18plus,
        isConsentModalAgreed: modalsStore.isConsentModalAgreed,
        subtitle,
        available: location.pathname.includes('/bar-menu/')
          ? !!item.isMenu &&
            (is_18plus ? modalsStore.isConsentModalAgreed : true)
          : is_18plus
          ? modalsStore.isConsentModalAgreed && available
          : available,
        isPromo,
        active: active === 'Y' ? 1 : 0,
        delivery,
        measureItem,
        rating_comments,
        comments_number,
        isBtnDisabled,
        productOfDay,
        isShowLongBuyBtn,
        onImgHover,
        omImgHoverEnd,
        isFavorite,
        productInfo,
        fileItem,
        sales_number,
        isProductBigCards,
        isExpressPickupAval: !!(isExpressPickupAval && available),
        productCount: productCount || 0,
        weight_of_goods,
        specialPriceData,
        priceBlockWeightPrice,
        gds_count,
        history,
        imgsArr,
        salesNumberInfo,
        badgesBottom,
        isShowPostponed,
        isPostponed,
        isShowTrash,
        task,
        deliveryDate: delivery?.date,
        totalWeight: `≈${(weight_of_goods ?? 0).toFixed(2)} ${
          measureItem?.symbol ?? ''
        }`,
        priceMeasure:
          priceBlockWeightPrice &&
          vesovoi === 'Y' &&
          (measureItem?.symbol === 'кг' || productInfo?.measure_id !== 2)
            ? `/${measureItem?.symbol}`
            : '',
        isVesovoi,
        isFreeze: war_id === 3,
        isExpressDeliveryAval,
        isDigital,
        isThanksForPurchasePage: location.pathname.includes('/cart/success/'),
        oldPriceField: _oldPriceField,
        currentPriceField: _currentPriceField,
        symbolPriceField: priceBlockWeightPrice
          ? symbolPriceField
          : symbolPriceField2,
        variants: item?.variants?.length ? item.variants : [],
        isLeaveCommentBtn,
        bestBeforeDate: expiration_title,
        supplyDate,
        leaveReviewHandler,
        onChangeWeightTabHandler,
        prepareToAddToFavourites,
        showCompleteModal: showCompleteModalFunc,
        changePostponedStatus,
        removeProduct: removeProductHandler,
        onAddToOrderFirst,
        onAddToOrderClickHandler: mutateOrder && onAddToOrderClickHandler,
        isPreOrder,
        isProductInCart,
        openInNewPage,
        multiPricePromo,
        isBigMobileCard,
        isStatic,
        onProductClick: () => {}
      };

      useEffect(() => {
        const disposer = autorun(() => {
          const {
            isConsentModalOpened,
            isConsentModalAgreed,
            setIsConsentModalOpened
          } = modalsStore;
          if (
            !!productInfo?.is_18plus &&
            !isConsentModalOpened &&
            !isConsentModalAgreed &&
            !IsPrerender()
          ) {
            setIsConsentModalOpened(true);
          }
        });
        return () => {
          disposer();
        };
      }, [modalsStore, productInfo?.is_18plus]);
      return (
        <>
          {!!leaveReviewItemInfoModal && (
            <ReviewProductModal
              onCloseModal={onReviewProductModalClose}
              itemId={id}
              productId={+id}
              commentType='Review'
              updateOrdersAfterSubmitReturnForm={onUpdateReview}
            />
          )}
          {showCompleteModal && (
            <SuccessModalWithTimer
              closeModal={changeCompleteModalState}
              text={'Вы подписались на рассылку'}
            />
          )}
          <ProductCardNew
            ref={ref}
            {...settings}
            isDesktop={windowWidth >= 1280}
          />
        </>
      );
    }
  )
);

export default ProductCard;
